<template>
  <transition name="terms-fade" appear>
    <div
      class="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="terms-modal-title"
      tabindex="-1"
      @keydown="handleKeydown"
    >
      <div class="modal-content">
      <h2 id="terms-modal-title" class="modal-title">
        {{ $t('checkout.termsTitle') }}
      </h2>

      <p v-if="!readOnly" class="terms-warning">
        {{ $t('checkout.termsWarningLine1') }}
        <br>
        {{ $t('checkout.termsWarningLine2') }}
      </p>

      <div class="terms-scroll-box" tabindex="-1" v-html="termsHtml" />

      <template v-if="readOnly">
        <div class="modal-actions">
          <button
            type="button"
            class="btn btn-primary"
            @click="$emit('cancel')"
          >
            {{ $t('entityChoice.closeTerms') }}
          </button>
        </div>
      </template>
      <template v-else>
        <div class="terms-accept-checkbox">
          <label class="checkbox-label">
            <input
              id="terms-accept-checkbox"
              type="checkbox"
              v-model="hasAccepted"
            />
            <span>{{ $t('checkout.termsIAccept') }}</span>
          </label>
        </div>

        <div class="modal-actions">
          <button
            type="button"
            class="btn btn-outline-secondary"
            @click="$emit('cancel')"
          >
            {{ $t('checkout.termsCancel') }}
          </button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="!hasAccepted"
            @click="$emit('accept')"
          >
            {{ $t('checkout.termsAccept') }}
          </button>
        </div>
      </template>
    </div>
  </div>
  </transition>
</template>

<script>
import { loadTermsHtml } from '@/utils/terms-loader'
import logoUrl from '@/assets/marathon-photos-logo.png'

export default {
  name: 'TermsModal',
  props: {
    readOnly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      hasAccepted: false,
      termsHtml: ''
    }
  },
  watch: {
    '$i18n.locale'() {
      this.fetchTerms()
    }
  },
  mounted() {
    this._previousFocus = document.activeElement
    document.body.style.overflow = 'hidden'
    this.fetchTerms()
    this.setInitialFocus()
  },
  beforeDestroy() {
    document.body.style.overflow = ''
    if (this._previousFocus && this._previousFocus.focus) {
      this._previousFocus.focus()
    }
  },
  methods: {
    /**
     * Loads terms HTML for the current locale and injects the logo URL.
     */
    async fetchTerms() {
      const html = await loadTermsHtml(this.$i18n.locale)
      this.termsHtml = html.replace(
        /data-logo/g,
        `src="${logoUrl}"`
      )
    },
    /**
     * Returns all tabbable elements within the modal (excludes disabled).
     * @returns {Element[]}
     */
    getFocusableElements() {
      return [...this.$el.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )].filter(el => !el.disabled)
    },
    /**
     * Sets initial focus on the checkbox (accept mode) or close button (read-only mode).
     */
    setInitialFocus() {
      this.$nextTick(() => {
        const target = this.readOnly
          ? this.$el.querySelector('.modal-actions .btn-primary')
          : this.$el.querySelector('#terms-accept-checkbox')
        if (target) {
          target.focus()
        }
      })
    },
    /**
     * Handles keydown events for focus trap and escape key.
     * @param {KeyboardEvent} e
     */
    handleKeydown(e) {
      if (e.key === 'Escape') {
        this.$emit('cancel')
        return
      }
      if (e.key !== 'Tab') return

      const focusable = this.getFocusableElements()
      if (!focusable.length) return

      const first = focusable[0]
      const last = focusable[focusable.length - 1]

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault()
          last.focus()
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }
  }
}
</script>

<!-- Unscoped styles for v-html terms content -->
<style>
.terms-scroll-box .terms-logo-row {
  text-align: right;
  margin-bottom: var(--spacing-md);
}

.terms-scroll-box .terms-logo {
  max-width: 160px;
  height: auto;
}

.terms-scroll-box .terms-heading-main {
  text-align: center;
  font-size: 1.2rem;
  font-weight: 600;
  margin: 0 0 var(--spacing-md) 0;
  color: var(--gray-900);
}

.terms-scroll-box .terms-bold {
  font-weight: 600;
}

.terms-scroll-box .terms-section-heading {
  text-decoration: underline;
  font-size: 0.95rem;
  font-weight: 400;
  margin: var(--spacing-md) 0 var(--spacing-sm) 0;
  color: var(--gray-800);
}

.terms-scroll-box .terms-list {
  padding-left: 1.8em;
  margin: 0 0 var(--spacing-sm) 0;
}

.terms-scroll-box .terms-list li {
  margin-bottom: var(--spacing-sm);
}

.terms-scroll-box .terms-sublist {
  padding-left: 1.5em;
  margin: var(--spacing-xs) 0 0 0;
}

.terms-scroll-box .terms-sublist li {
  margin-bottom: var(--spacing-xs);
}

.terms-scroll-box p {
  margin: 0 0 var(--spacing-md) 0;
}

.terms-scroll-box p:last-child {
  margin-bottom: 0;
}
</style>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1040;
  padding: var(--spacing-md);
}

.modal-content {
  background: var(--white);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-lg);
  max-width: 600px;
  width: 100%;
  padding: var(--spacing-xl);
  display: flex;
  flex-direction: column;
  max-height: 80vh;
}

.modal-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--gray-900);
  margin: 0 0 var(--spacing-sm) 0;
  flex-shrink: 0;
}

.terms-warning {
  color: var(--color-danger);
  font-weight: 500;
  margin: 0 0 var(--spacing-lg) 0;
  flex-shrink: 0;
}

.terms-scroll-box {
  overflow-y: auto;
  flex: 1;
  border: 1px solid var(--gray-300);
  border-radius: var(--border-radius-sm);
  padding: var(--spacing-md);
  margin-bottom: var(--spacing-sm);
  color: var(--gray-700);
  line-height: 1.6;
  min-height: 0;
}

.terms-accept-checkbox {
  margin: var(--spacing-md) 0 var(--spacing-sm) 0;
  flex-shrink: 0;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  cursor: pointer;
  font-weight: 500;
  color: var(--gray-900);
}

.checkbox-label input[type="checkbox"] {
  width: 1.1rem;
  height: 1.1rem;
  cursor: pointer;
  accent-color: var(--mp-blue);
}

.modal-actions {
  display: flex;
  gap: var(--spacing-sm);
  justify-content: flex-end;
  padding-top: var(--spacing-sm);
  flex-shrink: 0;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-sm) var(--spacing-md);
  border-radius: var(--border-radius-sm);
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s, border-color 0.2s;
}

.btn-primary {
  background-color: var(--mp-blue);
  color: var(--white);
  border: 1px solid var(--mp-blue);
}

.btn-primary:hover:not(:disabled) {
  background-color: var(--bg-blue);
}

.btn-primary:focus {
  outline: 2px solid var(--mp-blue);
  outline-offset: 2px;
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-outline-secondary {
  background: transparent;
  color: var(--gray-600);
  border: 1px solid var(--gray-400);
}

.btn-outline-secondary:hover {
  background: var(--gray-100);
}

.btn-outline-secondary:focus {
  outline: 2px solid var(--gray-600);
  outline-offset: 2px;
}

/* Transition */
.terms-fade-enter-active,
.terms-fade-leave-active {
  transition: opacity 0.25s;
}

.terms-fade-enter-active .modal-content,
.terms-fade-leave-active .modal-content {
  transition: transform 0.25s, opacity 0.25s;
}

.terms-fade-enter,
.terms-fade-leave-to {
  opacity: 0;
}

.terms-fade-enter .modal-content,
.terms-fade-leave-to .modal-content {
  transform: translateY(1rem);
  opacity: 0;
}

@media (max-width: 767px) {
  .modal-overlay {
    padding: var(--spacing-xs);
  }

  .modal-content {
    max-height: 95vh;
    padding: var(--spacing-md);
  }

  .terms-warning {
    margin-bottom: var(--spacing-sm);
  }

  .terms-accept-checkbox {
    margin: var(--spacing-sm) 0 var(--spacing-xs) 0;
  }

  .modal-actions {
    flex-direction: column;
  }

  .modal-actions .btn {
    width: 100%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .btn {
    transition: none;
  }

  .terms-fade-enter-active,
  .terms-fade-leave-active,
  .terms-fade-enter-active .modal-content,
  .terms-fade-leave-active .modal-content {
    transition: none;
  }
}
</style>
